import { Box } from "@chakra-ui/react";
import { useId } from "react";

import { MaplibreLegend, MaplibreLegendLayer } from "@nens/ranalib";

import { LayerControl, LayerControlHeader } from "@/components/layer-control";
import { useClickedOnVectorMap } from "@/hooks/use-clicked-on-vector-map";
import { useVectorPreview } from "@/hooks/use-vector-preview";
import { RanaMap } from "@/lib/rana-map";
import { RanaMapDisplayVectors } from "@/lib/rana-map/components/rana-map-vectors";
import { DisplayPopupInfo } from "@/lib/rana-popup/components/display-popup-info";
import { useShowsOnlyGraph } from "@/lib/rana-popup/hooks/use-shows-only-graph";
import { RanaPopup } from "@/lib/rana-popup/rana-popup";
import { VectorFileDescriptor } from "@/types/descriptor-types";

interface ProjectFilePreviewVectorProps {
  title: string;
  descriptor: VectorFileDescriptor;
}

export function ProjectFilePreviewVector({ descriptor, title }: ProjectFilePreviewVectorProps) {
  const mapId = `vector-map-${useId()}`;
  const { isLoading, bounds, layers, visibleLayers, setVisibleLayers, sprites } = useVectorPreview({
    descriptorId: descriptor.id,
  });

  const { clearMapClick, point, clickedPoint, setPoint, vectorInfoItems } = useClickedOnVectorMap(
    mapId,
    layers
  );

  const popupItems = vectorInfoItems.map((item) => ({ ...item, isVector: true as const }));
  const showsOnlyGraph = useShowsOnlyGraph(popupItems);

  const legendLayers = layers
    .map(({ layer, styleLayers, type }) => ({ layer, styleLayers, type }))
    .filter(
      (l): l is MaplibreLegendLayer =>
        visibleLayers.some((visibleLayer) => visibleLayer.layer === l.layer) &&
        l.styleLayers !== undefined
    );

  return (
    <RanaMap
      isLoading={isLoading}
      sprites={sprites}
      mapId={mapId}
      bounds={bounds}
      mapProps={{
        onLoad: () => setVisibleLayers(layers),
      }}
      deckGlProps={{}}
    >
      <RanaMap.MapScale />

      <RanaMap.ControlsTopLeft>
        <LayerControlHeader />
        <LayerControl
          descriptorId={descriptor.id}
          title={title}
          layers={layers.map(({ layer }) => layer)}
          visibleLayers={visibleLayers}
          setVisibleLayers={setVisibleLayers}
        />
      </RanaMap.ControlsTopLeft>

      {legendLayers.length > 0 && (
        <RanaMap.ControlsBottomLeft>
          <Box p={4} maxH="2xs">
            <MaplibreLegend layers={legendLayers} mapId={mapId} />
          </Box>
        </RanaMap.ControlsBottomLeft>
      )}

      <RanaMapDisplayVectors
        // Reverse the layers so that the first layer is on top
        layers={[...layers].reverse().map((fullLayer) => {
          const isVisible = visibleLayers.some(
            (visibleLayer) => visibleLayer.layer === fullLayer.layer
          );
          return { ...fullLayer, isVisible };
        })}
      />

      {vectorInfoItems.length > 0 && point && (
        <RanaPopup
          anchor="bottom"
          point={point}
          onClose={clearMapClick}
          header={showsOnlyGraph ? null : undefined}
        >
          <DisplayPopupInfo
            items={popupItems}
            setPoint={setPoint}
            clickedPoint={clickedPoint}
            isCompact
          />
        </RanaPopup>
      )}
    </RanaMap>
  );
}
